// The live audience-size readout under the rule editor: the matched-user count on its labelled
// scale, the share of total reach, and the calculating / model-pending states. The scale's
// breakpoints live in `@/lib/audience-gauge`; `audience-gauge.test.ts` checks that every one is
// still named in the mark labels below.

import { Icons } from '@/components/primitives';
import type { InsightStatus } from '@/api/types.gen';
import { audienceGaugeFill } from '@/lib/audience-gauge';
import { fitPendingCopy, fitPendingHeading } from '@/lib/lookalike-model';

export interface AudienceSizeGaugeProps {
  size: number | null;
  totalReach: number | null;
  compact?: boolean;
  loading: boolean;
  error: Error | null;
  /** An unfinished rule's sentence. It outranks `error`: nothing was estimated, nothing failed. */
  incomplete?: string | null;
  modelStatus?: InsightStatus | null;
  fitRequestedAt?: string | null;
}

export function AudienceSizeGauge({
  size,
  totalReach,
  compact = false,
  loading,
  error,
  incomplete = null,
  modelStatus = null,
  fitRequestedAt = null,
}: AudienceSizeGaugeProps) {
  const hasEstimate = typeof size === 'number';
  const hasReach = typeof totalReach === 'number' && totalReach > 0;
  const modelPending = modelStatus && modelStatus !== 'ready';
  const percent = hasEstimate && hasReach ? (size / totalReach) * 100 : null;
  const badge = incomplete
    ? 'INCOMPLETE'
    : error
    ? 'ERROR'
    : loading
      ? 'CALCULATING'
      : modelPending
        ? 'PENDING'
        : hasEstimate
          ? 'UPDATED'
          : 'WAITING';
  const badgeClass = incomplete
    ? 'ps ps-idle'
    : error
    ? 'ps audience-estimate-status-error'
    : loading
      ? 'ps ps-processing'
      : modelPending
        ? 'ps ps-idle'
      : hasEstimate
        ? 'ps ps-ready'
        : 'ps ps-idle';

  return (
    <div
      className={`facet audience-estimate ${loading ? 'is-calculating' : ''}`}
      style={compact ? {margin:0} : {}}
      aria-busy={loading}
    >
      <div className="facet-hd">
        <h5>{modelPending ? 'Lookalike audience size' : 'Live audience size'}</h5>
        <span className={badgeClass} role="status" aria-live="polite">
          <span className={`dot ${loading ? 'pulse' : ''}`}/>
          {badge}
        </span>
      </div>
      {loading ? (
        <div className="audience-estimate-progress">
          <span className="audience-estimate-spinner" aria-hidden="true"/>
          <span className="audience-estimate-progress-copy">
            <strong>Calculating audience size…</strong>
            <span>Applying every rule to the latest available audience data</span>
          </span>
        </div>
      ) : modelPending ? (
        <div className="audience-estimate-progress">
          <span className="audience-planner-empty-icon" aria-hidden="true">{Icons.history}</span>
          <span className="audience-estimate-progress-copy">
            <strong>{fitPendingHeading(fitRequestedAt)}</strong>
            <span>{fitPendingCopy(fitRequestedAt)}</span>
          </span>
        </div>
      ) : (
        <div style={{display:'flex',alignItems:'baseline',gap:10,flexWrap:'wrap'}}>
          <div style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:compact ? 28 : 32,letterSpacing:'.02em'}} className="tnum">
            {hasEstimate ? size.toLocaleString() : '—'}
          </div>
          <div style={{fontFamily:'var(--font-mono)',fontSize:10.5,color:'var(--ink-2)',letterSpacing:'.1em',textTransform:'uppercase'}}>matched users</div>
          <div className="audience-estimate-share" style={{marginLeft:'auto',fontFamily:'var(--font-mono)',fontSize:10.5,color:'var(--ink-2)'}}>
            ≈ <b style={{color:'var(--ink)'}}>{percent === null ? '—' : `${percent.toFixed(1)}%`}</b> of total reach
          </div>
        </div>
      )}
      <div className="gauge-track">
        <div
          className={`gauge-fill ${loading ? 'is-calculating' : ''}`}
          // Positioned on the labelled matched-user scale below, NOT on
          // `percent` — that is share-of-reach, and driving the bar with it put
          // a 998K audience on "5M+ (too broad)".
          style={{width: loading ? '32%' : `${audienceGaugeFill(size)}%`}}
        />
      </div>
      <div className="gauge-marks"><span><span className="phone-long">10K (too narrow)</span><span className="phone-short">10K · too narrow</span></span><span className="gauge-mark-tick">200K</span><span><span className="phone-long">500K (sweet spot)</span><span className="phone-short">200K–500K · sweet spot</span></span><span className="gauge-mark-tick">2M</span><span><span className="phone-long">5M+ (too broad)</span><span className="phone-short">5M+ · too broad</span></span></div>
      {incomplete
        ? <div className="muted audience-estimate-incomplete" style={{marginTop:8,fontSize:11.5}}>{incomplete}</div>
        : error && <div style={{marginTop:8,color:'var(--bad)',fontSize:11.5}}>{error.message}</div>}
    </div>
  );
}
